import { useState } from 'react';
import { IconAlertTriangle, IconArrowLeft, IconBolt, IconCamera, IconCheck, IconClock, IconDoorEnter, IconDoorExit, IconFileDescription, IconMapPin, IconNavigation, IconPhone, IconReceipt, IconUser } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Page, PageHeader } from '@hana/shell';
import { Button, Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import { TYPE_LABEL, isUrgent } from './data';
import { FEE_CODES, FIELD_TASKS, INCIDENT_KINDS, NOW, STEPS, type FieldTask } from './fieldData';
import s from './field.module.css';
import './i18n';

// Mint-OpsField-Phone (390): Việc hôm nay của OPS-FLD — danh sách theo thứ tự đường đi + chi tiết việc.
// Chỉ đọc: Check-in / Đến nơi / Xong bước chỉ đổi trạng thái cục bộ + toast (POST /v1/field/events khi G-OD3 mở ghi).
// Vị trí chỉ ghi sau khi đồng ý (cờ B-GPS1 của Mee) — bản này không ghi vị trí.

type Shift = 'in' | 'none' | 'out';
type Sheet = 'none' | 'fee' | 'incident' | 'consent';
const q = (k: string) => new URLSearchParams(location.search).get(k);
const hh = (v: number): string => `${String(Math.floor(v)).padStart(2, '0')}:${String(Math.round((v % 1) * 60)).padStart(2, '0')}`;

export function Field() {
  const t = useT();
  const toast = useToast();
  const rawState = q('state');
  const state: ScreenState = rawState === 'loading' || rawState === 'empty' || rawState === 'error' || rawState === 'noperm' ? rawState : 'ready';
  const money = q('money') !== 'hide';
  const ro = state === 'noperm';
  const [shift, setShift] = useState<Shift>((['in', 'none', 'out'] as const).find((x) => x === q('shift')) ?? 'in');
  const [consent, setConsent] = useState(false);
  const [sheet, setSheet] = useState<Sheet>((['fee', 'incident', 'consent'] as const).find((x) => x === q('sheet')) ?? 'none');
  const [sel, setSel] = useState<string | null>(q('task') ?? (q('view') === 'detail' ? 'NCT2610012' : null));
  const [stepOv, setStepOv] = useState<Record<string, number>>({});
  const [fee, setFee] = useState('POB');
  const [payer, setPayer] = useState<'co' | 'me'>('co');
  const [inc, setInc] = useState(0);
  const flash = (m: string) => toast({ msg: m });

  const all = FIELD_TASKS.map((r) => {
    const steps = STEPS[r.kind];
    const step = Math.min(stepOv[r.id] ?? r.step0, steps.length);
    return { ...r, steps, step, done: step >= steps.length };
  });
  type Row = (typeof all)[number];
  const nextI = all.findIndex((r) => !r.done);
  const doneN = all.filter((r) => r.done).length;
  const ar = all.find((r) => r.id === sel) ?? null;

  const stOf = (r: Row): [string, string] => {
    if (r.done) return [t('opsf.st.done'), 'var(--ok)'];
    if (r.step > 0) return [t('opsf.st.doing', { n: r.step, m: r.steps.length }), 'var(--warn)'];
    if (r.a < NOW) return [t('opsf.st.late', { n: Math.round((NOW - r.a) * 60) }), 'var(--err)'];
    return [t('opsf.st.wait'), 'var(--lb)'];
  };
  const advance = (r: Row) => {
    if (ro) return;
    if (shift !== 'in') { flash(t('opsf.needShift')); return; }
    const n = r.step + 1;
    setStepOv({ ...stepOv, [r.id]: n });
    setSel(r.id);
    flash(r.step === 0 ? t('opsf.arrived', { h: hh(NOW) }) : n >= r.steps.length ? t('opsf.finished', { id: r.id }) : t('opsf.stepDone', { k: r.steps[r.step] ?? '' }));
  };
  const shiftAct = () => {
    if (ro) return;
    if (shift === 'in') { setShift('out'); flash(t('opsf.outDone')); return; }
    if (!consent) { setSheet('consent'); return; }
    setShift('in');
    flash(t('opsf.inDone', { h: hh(NOW) }));
  };
  const nav = (r: FieldTask) => flash(t('opsf.nav', { p: r.place.split(' · ')[0] ?? '' }));

  const shiftK = shift === 'in' ? t('opsf.shift.in') : shift === 'out' ? t('opsf.shift.out') : t('opsf.shift.none');
  const shiftSub = shift === 'in' ? 'Bến Nghé, Q1' : shift === 'out' ? t('opsf.shift.outSub') : t('opsf.shift.noneSub');
  const gpsTip = t('opsf.gpsTip');

  const header = <PageHeader title={ar ? TYPE_LABEL[ar.kind] : t('opsf.title')} sub={ar ? `${ar.id} · ${ar.job}` : t('opsf.today')} lead={ar ? <button type="button" className={s.btn} aria-label={t('opsf.back')} title={t('opsf.back')} onClick={() => setSel(null)}><IconArrowLeft size={18} aria-hidden="true" /></button> : undefined} />;

  const list = (
    <>
      <section className={`${s.card} ${s.shift}`} aria-label={t('opsf.shift.aria')}>
        <span className={`${s.shIc} ${shift === 'in' ? s.shOn : ''}`}><IconMapPin size={20} aria-hidden="true" /></span>
        <span className={s.shTx}><b>{shiftK}</b><span className={s.sub} title={gpsTip}><IconMapPin size={13} aria-hidden="true" />{shiftSub}</span></span>
        <button type="button" className={`${s.btn} ${shift === 'in' ? '' : s.btnP}`} disabled={ro} onClick={shiftAct}>
          {shift === 'in' ? <IconDoorExit size={16} aria-hidden="true" /> : <IconDoorEnter size={16} aria-hidden="true" />}
          {shift === 'in' ? t('opsf.checkout') : shift === 'out' ? t('opsf.checkinAgain') : t('opsf.checkin')}
        </button>
      </section>
      <div className={s.prog} role="img" aria-label={t('opsf.progAria', { n: doneN, m: all.length })}>
        <div className={s.segs}>{all.map((r) => <i key={r.id} className={r.done ? s.g : r.step > 0 ? s.b : ''} />)}</div>
        <span className={s.progK}>{t('opsf.progK', { n: doneN, m: all.length })}</span>
      </div>
      <ol className={s.list} aria-label={t('opsf.listAria')}>
        {all.map((r, j) => {
          const [stK, stC] = stOf(r);
          const isNext = j === nextI;
          const cur = ar?.id === r.id;
          const lateT = !r.done && r.a < NOW && r.step === 0;
          return (
            <li key={r.id} className={`${s.card} ${s.row} ${cur ? s.rowCur : ''} ${r.done ? s.rowDone : ''}`}>
              <button type="button" className={s.open} aria-current={cur} onClick={() => setSel(r.id)}>
                <div className={s.top}>
                  <span className={`${s.n} ${r.done ? s.nDone : isNext ? s.nNext : ''}`} aria-label={t('opsf.order', { n: j + 1 })}>{r.done ? '✓' : j + 1}</span>
                  <span className={`${s.ty} ${isUrgent(r.kind) ? s.tyU : ''}`} title={TYPE_LABEL[r.kind]} style={{ background: `color-mix(in srgb, ${isUrgent(r.kind) ? 'var(--err)' : 'var(--d-blue)'} 14%, transparent)` }}>{r.kind}</span>
                  {isUrgent(r.kind) && !r.done && <span className={s.urg} title={t('opsf.urgent')}><IconBolt size={12} aria-hidden="true" />{t('opsf.urgTag')}</span>}
                  <span className={s.st} style={{ color: stC }}><i aria-hidden="true" />{stK}</span>
                </div>
                <b className={s.place}>{r.place}</b>
                <div className={s.meta}><span>{r.job}</span><span className={`${s.win} ${lateT ? s.late : ''}`} title={t('opsf.window')}><IconClock size={13} aria-hidden="true" />{hh(r.a)}–{hh(r.b)}</span></div>
                <div className={s.bar}>{r.steps.map((k, i) => <i key={k} className={i < r.step ? s.g : i === r.step && r.step > 0 && !r.done ? s.b : ''} />)}</div>
              </button>
              {isNext && (
                <div className={s.acts}>
                  <button type="button" className={s.btn} onClick={() => nav(r)}><IconNavigation size={16} aria-hidden="true" />{t('opsf.directions')} · {r.dist}</button>
                  <button type="button" className={`${s.btn} ${s.btnP}`} disabled={ro} onClick={() => advance(r)}>{r.step === 0 ? <IconMapPin size={16} aria-hidden="true" /> : <IconCheck size={16} aria-hidden="true" />}{r.step === 0 ? t('opsf.arrive') : t('opsf.stepGo')}</button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </>
  );

  const detail = (r: Row) => {
    const lateT = !r.done && r.a < NOW && r.step === 0;
    const advK = r.done ? t('opsf.allDone') : r.step === 0 ? t('opsf.arrive') : r.step === r.steps.length - 1 ? t('opsf.finishStep', { k: r.steps[r.step] ?? '' }) : t('opsf.doStep', { k: r.steps[r.step] ?? '' });
    const [jobNo = '', cust = ''] = r.job.split(' · ');
    const sub = [money ? { k: t('opsf.fee'), Ico: IconReceipt, on: () => setSheet('fee'), err: false } : null, { k: t('opsf.photo'), Ico: IconCamera, on: () => flash(t('opsf.photoDone', { id: r.id })), err: false }, { k: t('opsf.incident'), Ico: IconAlertTriangle, on: () => setSheet('incident'), err: true }].filter((x): x is NonNullable<typeof x> => !!x);
    return (
      <>
        <section className={s.card} aria-label={t('opsf.routeAria')}>
          <div className={s.map} role="img" aria-label={t('opsf.mapAria', { p: r.place, d: r.dist })}>
            <svg aria-hidden="true" viewBox="0 0 358 132" preserveAspectRatio="none"><path className={s.road} d="M0 40 L358 22 M0 96 L358 110 M70 0 L96 132 M210 0 L188 132 M300 0 L322 132" strokeWidth="7" fill="none" /><path className={s.route} d="M72 104 C120 98 150 70 196 64 S 268 46 286 40" strokeWidth="4" fill="none" strokeLinecap="round" /><circle className={s.pinDot} cx="72" cy="104" r="7" strokeWidth="3" /></svg>
            <span className={s.dest} aria-hidden="true" />
            <span className={s.dist}>{r.dist} · {r.eta}</span>
          </div>
          <div className={s.where}>
            <span><b>{r.place.split(' · ').slice(0, 2).join(' · ')}</b><span className={s.sub}>{r.addr}</span></span>
            <button type="button" className={s.btn} onClick={() => nav(r)}><IconNavigation size={16} aria-hidden="true" />{t('opsf.directions')}</button>
          </div>
        </section>
        <div className={s.chips}>
          <span className={`${s.chip} ${lateT ? s.chipE : ''}`} title={t('opsf.window')}><IconClock size={14} aria-hidden="true" />{hh(r.a)}–{hh(r.b)}</span>
          {isUrgent(r.kind) && <span className={`${s.chip} ${s.chipE}`} title={t('opsf.urgentBy')}><IconBolt size={14} aria-hidden="true" />{t('opsf.urgTag')}</span>}
          <span className={s.chip} title="Job"><IconFileDescription size={14} aria-hidden="true" />{jobNo}</span>
          <span className={s.chip} title={t('opsf.customer')}><IconUser size={14} aria-hidden="true" />{cust}</span>
        </div>
        {r.dev > 300 && r.step > 0 && <div className={s.dev} role="status"><IconAlertTriangle size={16} aria-hidden="true" />{t('opsf.dev', { m: r.dev })}</div>}
        <section className={`${s.card} ${s.steps}`} aria-label={t('opsf.steps')}>
          <div className={s.stH}>{t('opsf.steps')}</div>
          <ol className={s.stL}>
            {r.steps.map((k, j) => {
              const dn = j < r.step; const cu = j === r.step && !r.done;
              return (
                <li key={k}>
                  <span className={`${s.dot} ${dn ? s.dotDone : cu ? s.dotCur : ''}`}>{dn ? '✓' : j + 1}</span>
                  <span className={`${s.stK} ${cu ? s.stCur : ''}`}>{k}</span>
                  <span className={s.when}>{dn ? (j === 0 ? hh(NOW) : t('opsf.st.done').toLowerCase()) : cu ? t('opsf.doingNow') : ''}</span>
                </li>
              );
            })}
          </ol>
        </section>
        <div className={s.sub3} style={{ gridTemplateColumns: `repeat(${sub.length}, 1fr)` }} role="group" aria-label={t('opsf.more')}>
          {sub.map(({ k, Ico, on, err }) => <button key={k} type="button" className={`${s.subB} ${err ? s.subE : ''}`} disabled={ro} onClick={on}><Ico size={20} aria-hidden="true" />{k}</button>)}
        </div>
        <div className={s.by}><span>{t('opsf.by', { who: r.by })}</span><a className={s.call} href="tel:" aria-label={t('opsf.call')} title={t('opsf.call')}><IconPhone size={18} aria-hidden="true" /></a></div>
        <div className={s.main}>
          <button type="button" className={s.mainB} disabled={ro || r.done} onClick={() => advance(r)}>{r.step === 0 ? <IconMapPin size={18} aria-hidden="true" /> : <IconCheck size={18} aria-hidden="true" />}{advK}</button>
        </div>
      </>
    );
  };

  let body;
  if (state === 'loading') body = <>{[1, 2, 3, 4].map((k) => <div key={k} className={s.skel} aria-busy="true"><i style={{ width: '60%' }} /><i style={{ width: '80%' }} /><i style={{ width: '40%' }} /></div>)}</>;
  else if (state === 'empty') body = <StateView state="empty" title={t('opsf.empty')} desc={t('opsf.emptyDesc')} />;
  else if (state === 'error') body = <StateView state="error" title={t('opsf.error')} desc={t('opsf.errorDesc')} action={<Button variant="secondary" onClick={() => flash(t('opsd.noWrite'))}>{t('opsf.retry')}</Button>} />;
  else body = ar ? detail(ar) : list;

  return (
    <Page>
      {header}
      <div className={s.page}>
        {ro && <div role="note" className={s.note}>{t('opsf.readonly')}</div>}
        {body}
      </div>
      <Modal open={sheet === 'fee' && !!ar} onOpenChange={(o) => !o && setSheet('none')} title={`${t('opsf.fee.title')} · ${ar?.id ?? ''}`}
        footer={<div className={s.foot}><Button variant="secondary" onClick={() => flash(t('opsf.feeSnap'))}><IconCamera size={16} aria-hidden="true" />{t('opsf.feeSnapK')}</Button><Button variant="primary" onClick={() => { setSheet('none'); flash(t('opsf.feeSaved')); }}>{t('opsf.feeSave')}</Button></div>}>
        <div className={s.form}>
          <div><div className={s.lbl}>{t('opsf.feeCode')}</div><div className={s.opts} role="radiogroup" aria-label={t('opsf.feeCode')}>{FEE_CODES.map(([v, k]) => <button key={v} type="button" role="radio" aria-checked={fee === v} className={`${s.opt} ${fee === v ? s.optOn : ''}`} onClick={() => setFee(v)}>{k}</button>)}</div></div>
          <label><div className={s.lbl}>{t('opsf.feeAmount')} <span className={s.errT}>*</span></div><input className={s.fld} inputMode="numeric" placeholder={t('opsf.feePh')} /></label>
          <div><div className={s.lbl}>{t('opsf.payer')}</div><div className={s.opts} role="radiogroup" aria-label={t('opsf.payer')}>{([['co', t('opsf.payer.co')], ['me', t('opsf.payer.me')]] as const).map(([v, k]) => <button key={v} type="button" role="radio" aria-checked={payer === v} className={`${s.opt} ${payer === v ? s.optOn : ''}`} onClick={() => setPayer(v)}>{k}</button>)}</div></div>
        </div>
      </Modal>
      <Modal open={sheet === 'incident' && !!ar} onOpenChange={(o) => !o && setSheet('none')} title={`${t('opsf.incident')} · ${ar?.id ?? ''}`}
        footer={<div className={s.foot}><Button variant="secondary" aria-label={t('opsf.photo')} onClick={() => flash(t('opsf.incSnap'))}><IconCamera size={16} aria-hidden="true" /></Button><Button variant="primary" onClick={() => { setSheet('none'); flash(t('opsf.incSent')); }}>{t('opsf.incSend')}</Button></div>}>
        <div className={s.form}>
          <div className={`${s.opts} ${s.opts1}`} role="radiogroup" aria-label={t('opsf.incKind')}>{INCIDENT_KINDS.map((k, j) => <button key={k} type="button" role="radio" aria-checked={inc === j} className={`${s.opt} ${inc === j ? s.optOn : ''}`} onClick={() => setInc(j)}>{k}</button>)}</div>
          <label><div className={s.lbl}>{t('opsf.note')}</div><textarea className={s.fld} rows={2} placeholder={t('opsf.notePh')} /></label>
        </div>
      </Modal>
      <Modal open={sheet === 'consent'} onOpenChange={(o) => !o && setSheet('none')} title={t('opsf.consent.title')}
        footer={<div className={s.foot}><Button variant="secondary" onClick={() => setSheet('none')}>{t('opsf.later')}</Button><Button variant="primary" onClick={() => { setConsent(true); setSheet('none'); setShift('in'); flash(t('opsf.inDone', { h: hh(NOW) })); }}>{t('opsf.agree')}</Button></div>}>
        <ul className={s.consent}><li>{t('opsf.consent.1')}</li><li>{t('opsf.consent.2')}</li><li>{t('opsf.consent.3')}</li></ul>
      </Modal>
    </Page>
  );
}
